import { AlertTriangle, XCircle } from 'lucide-react';
import type { PlaybookIssue } from '@/hooks/useRoutines';

interface Props {
  issues: PlaybookIssue[];
  /** Springt im Editor zur Zeile (nur gesetzt, wenn ein Editor offen ist). */
  onJumpToLine?: (line: number) => void;
  className?: string;
}

const sourceLabel: Record<PlaybookIssue['source'], string> = {
  yaml: 'YAML',
  meta: 'Meta',
  ansible: 'Ansible',
};

/**
 * PlaybookIssueList - Validierungs-Ergebnis als Liste mit Zeilennummern.
 *
 * Zeilennummern sind Buttons, die im Editor zur Fehlerstelle springen.
 */
export function PlaybookIssueList({ issues, onJumpToLine, className }: Props): JSX.Element | null {
  if (issues.length === 0) return null;

  return (
    <ul
      className={['flex flex-col gap-1.5 text-xs', className ?? ''].join(' ')}
      aria-label="Validierungs-Meldungen"
      data-testid="playbook-issues"
    >
      {issues.map((issue, idx) => {
        const isError = issue.severity === 'error';
        const Icon = isError ? XCircle : AlertTriangle;
        return (
          <li
            key={idx}
            className={['flex items-start gap-2', isError ? 'text-danger' : 'text-warning'].join(
              ' '
            )}
            data-testid={`playbook-issue-${issue.severity}`}
          >
            <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              <span className="font-medium uppercase tracking-wide">
                [{sourceLabel[issue.source] ?? issue.source}]
              </span>{' '}
              {issue.line != null &&
                (onJumpToLine ? (
                  <button
                    type="button"
                    onClick={() => onJumpToLine(issue.line as number)}
                    className="rounded-sm font-mono underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={`Zu Zeile ${issue.line} springen`}
                  >
                    Zeile {issue.line}
                    {issue.column != null ? `:${issue.column}` : ''}
                  </button>
                ) : (
                  <span className="font-mono">
                    Zeile {issue.line}
                    {issue.column != null ? `:${issue.column}` : ''}
                  </span>
                ))}
              {issue.line != null && ' — '}
              <span className="text-text">{issue.message}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
